import { Fragment, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ReportColumnMeta } from '../api/reports.js';
import { text, money, num, datetime, datetimeWithSeconds, dateOnly } from '../lib/format.js';
import { GradeBadge } from './GradeBadge.js';

/**
 * 표 한 칸을 그린다. 정의(ReportColumnMeta)의 type 에 따라 모양이 갈린다.
 * 조회 화면(/reports/:id)과 코드 조회 도구(/lookup/:id)가 같이 쓴다.
 */

/**
 * 이미 완성된 주소인가 - http(s), 프로토콜 생략(//cdn...), 루트 절대경로(/files/...).
 * 이런 값은 주소 **한 조각**이 아니라 주소 **전체**라 인코딩하면 안 된다.
 * javascript: · data: 는 여기 안 걸린다 - 그건 아래에서 그대로 인코딩돼
 * 실행 통로가 되지 못한다.
 */
const WHOLE_URL = /^(?:https?:\/\/|\/\/|\/)/i;

/**
 * '{goods_idx}/{value}' 같은 틀에 행의 값을 채운다. {value} 는 그 컬럼 자신의 값.
 *
 * 채워 넣는 값은 보통 주소의 한 조각(파일명·상품번호)이라 encodeURIComponent 로
 * 감싼다 - 파일명에 공백이나 한글이 섞여도 주소가 안 깨진다.
 *
 * 그런데 srcTemplate 이 '{value}' 하나뿐인 정의(sales-analysis 의 썸네일 등)는
 * DB 값 자체가 완성된 URL 이다 - 여기에 encodeURIComponent 를 걸면
 * 'https://cdn...' 이 'https%3A%2F%2Fcdn...' 이 돼서 상대경로로 읽히고 이미지가
 * 전부 깨졌다. 그래서 값이 이미 주소 전체면 그대로 둔다.
 */
function fillTemplate(template: string, raw: unknown, row: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (_m, k: string) => {
    const value = String(k === 'value' ? raw : (row[k] ?? ''));
    return WHOLE_URL.test(value) ? value : encodeURIComponent(value);
  });
}

/**
 * DB 의 상품명(shop_goods.gname)에는 '신상품' 같은 아이콘이 <img> 태그째로
 * 섞여 있다 - 원본 PHP 가 그 값을 그대로 뱉어서 브라우저가 그림으로 그렸다.
 *
 * 태그를 통째로 HTML 로 심으면(dangerouslySetInnerHTML) 상품명에 뭐가 들어
 * 있든 같이 실행된다. 그래서 <img> 의 src 만 뽑아 진짜 <img> 로 그리고,
 * 나머지는 전부 그냥 글자로 둔다 - 심을 수 있는 건 이미지 주소 하나뿐이다.
 */
const IMG_TAG = /<img\b[^>]*>/gi;

function imgSrcOf(tag: string): string | undefined {
  const m = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(tag);
  const url = m?.[1] ?? m?.[2] ?? m?.[3];
  // http(s) 만 허용한다. javascript: · data: 는 그림이 아니라 실행 통로다.
  return url && /^https?:\/\//i.test(url) ? url : undefined;
}

function RichText({ value }: { value: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of value.matchAll(IMG_TAG)) {
    const at = m.index;
    if (at > last) parts.push(value.slice(last, at));
    const src = imgSrcOf(m[0]);
    // src 를 못 읽었거나 http(s) 가 아니면 그 태그는 그냥 지운다 (원본에서도
    // 깨진 그림 자리였다).
    if (src) parts.push(<img className="text-icon" src={src} alt="" loading="lazy" />);
    last = at + m[0].length;
  }
  if (last < value.length) parts.push(value.slice(last));
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </>
  );
}

/**
 * 마우스를 올리면 옆에 상세 이미지를 띄운다 (원본 zaiko/filter_zaiko_list.php 의
 * showImgLayerMulti/hideImgLayer).
 *
 * 원본과 같은 규칙:
 *   - 띄우는 건 썸네일을 확대한 게 아니라 **상품설명에서 뽑은 상세 이미지 전부**다
 *     (previewImages). 위에서 아래로 쌓아 보여준다.
 *   - 상세 이미지가 하나도 없으면 미리보기를 아예 안 띄운다 (원본 :188 의 else -
 *     onmouseover 를 안 달았다).
 *   - 이미지가 있으면 썸네일 위에 'COUNT : n' 을 적는다 (원본 :178).
 *
 * .table-wrap 이 overflow-x:auto 라 표 안에 그대로 띄우면 스크롤 밖으로 나가는
 * 순간 잘린다 - body 에 portal 로 그려서 표의 스크롤·자르기와 무관하게 뜨게 한다.
 */
function ImageThumb({
  src,
  previewImages,
  width,
}: {
  src: string;
  previewImages?: string[];
  /** 썸네일 폭(px). 생략하면 CSS 기본값(30px). */
  width?: number;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // 원본은 previewImages 를 안 넘기는 화면(다른 리포트들)에서도 썸네일을 쓴다.
  // 그럴 땐 썸네일 자신을 크게 보여주는 게 자연스럽다.
  const shots = previewImages ?? [src];
  const hoverable = shots.length > 0;
  // 상세 이미지 목록(showImgLayerMulti)은 원본이 인라인 style="width:50px" 로
  // 작게 쌓았다 - 개수가 많을 수 있어서다. previewImages 가 없을 때(다른
  // 화면의 기본 확대 미리보기)는 그대로 크게 둔다.
  const compact = previewImages !== undefined;

  return (
    <>
      {previewImages && previewImages.length > 0 && (
        <span className="thumb-count">COUNT : {previewImages.length}</span>
      )}
      <img
        className="thumb"
        src={src}
        alt=""
        loading="lazy"
        style={width ? { width, height: 'auto' } : undefined}
        onMouseEnter={
          hoverable
            ? (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                // 원본과 같이 이미지 오른쪽 위에 띄운다. scrollX/Y 는 body portal 이
                // 문서 좌표로 그려지기 때문에 필요하다 (뷰포트 좌표만 쓰면 스크롤한
                // 만큼 어긋난다).
                setPos({ top: rect.top + window.scrollY, left: rect.right + window.scrollX + 10 });
              }
            : undefined
        }
        onMouseLeave={hoverable ? () => setPos(null) : undefined}
      />
      {pos &&
        createPortal(
          <div
            className={compact ? 'thumb-preview thumb-preview--compact' : 'thumb-preview'}
            style={{ top: pos.top, left: pos.left }}
          >
            {shots.map((s, i) => (
              <img key={`${s}-${i}`} src={s} alt="" />
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

export function Cell({
  column,
  row,
  totals,
  rowNumber,
}: {
  column: ReportColumnMeta;
  row: Record<string, unknown>;
  totals?: Record<string, number>;
  /** rowNumber 타입 전용: SQL 이 아니라 화면이 매긴 행 번호 (ReportPage 참고). */
  rowNumber?: number;
}) {
  // 비율 컬럼은 SQL 에 없다. 조건에 걸린 전체 합계로 나눠서 화면에서 만든다.
  if (column.percentOf) {
    const value = Number(row[column.percentOf] ?? 0);
    const total = Number(totals?.[column.percentOf] ?? 0);
    if (!total || Number.isNaN(value)) return <span className="td-muted">-</span>;
    return <>{((value / total) * 100).toFixed(2)}%</>;
  }

  // 다른 컬럼에서 정규식으로 뽑는 값 (원본이 preg_match 로 하던 것).
  if (column.extract) {
    const src = String(row[column.extract.from] ?? '');
    const m = new RegExp(column.extract.pattern).exec(src);
    return m?.[1] ? <>{m[1]}</> : <span className="td-muted">-</span>;
  }

  if (column.type === 'rowNumber') return <>{rowNumber ?? '-'}</>;

  const raw = row[column.key];

  switch (column.type) {
    case 'money':
      return <>{money(raw as number | null)}</>;
    case 'number':
      return <>{num(raw as number | null)}</>;
    case 'date': {
      const formatted = dateOnly(raw as string | null);
      // 예약날짜처럼 '값 없음' 자체가 뜻이 있는 칸은 조용한 '-' 대신 눈에 띄는
      // 말을 보여준다 (예: 예약없음, 빨간 글자).
      if (formatted === '-' && column.emptyLabel) {
        return <span className="td-danger">{column.emptyLabel}</span>;
      }
      return <span className="td-muted">{formatted}</span>;
    }
    case 'datetime':
      return (
        <span className="td-muted">
          {column.withSeconds ? datetimeWithSeconds(raw as string | null) : datetime(raw as string | null)}
        </span>
      );
    case 'grade':
      return <GradeBadge grade={raw as string | null} />;
    case 'image': {
      if (!raw) return <>-</>;
      // DB 에는 파일명만 들어 있다. 원본이 붙이던 CDN 경로를 정의에서 받아 쓴다.
      const src = fillTemplate(column.srcTemplate ?? '{value}', raw, row);
      const preview = column.previewImagesKey
        ? (row[column.previewImagesKey] as string[] | undefined) ?? []
        : undefined;
      return <ImageThumb src={src} previewImages={preview} width={column.imageWidth} />;
    }
    case 'link': {
      if (!raw) return <>-</>;
      const href = fillTemplate(column.hrefTemplate ?? '{value}', raw, row);
      return (
        <a className="link" href={href} target="_blank" rel="noreferrer">
          {/* gname(상품명)처럼 <img> 아이콘이 섞인 값도 링크로 나갈 수 있다 -
              기본 텍스트 칸과 같은 RichText 로 그려서 아이콘이 그대로 보이게 한다. */}
          <RichText value={String(raw)} /> ↗
        </a>
      );
    }
    case 'enum': {
      // 원본이 PHP 배열로 코드값을 바꿔 보여주던 자리. 표에 없는 값은
      // 원본 switch 의 default 처럼 코드값을 그대로 드러낸다.
      const key = String(raw ?? '');
      if (key === '') return <span className="td-muted">-</span>;
      const label = column.valueLabels?.[key] ?? key;
      // badge 타입처럼 badgeColors 를 뒀으면 그 색으로 글자만 강조한다 (뱃지 알약
      // 모양 없이) - 예: 취소는 빨간 글자, 입금확인은 초록 글자로.
      const tone = column.badgeColors?.[key];
      if (tone === 'danger' || tone === 'ok' || tone === 'info') {
        return <span className={`td-${tone}`}>{label}</span>;
      }
      return <>{label}</>;
    }
    case 'badge': {
      const key = String(raw ?? '');
      const tone = column.badgeColors?.[key] ?? 'muted';
      // 값이 0/빈값이면 뱃지 대신 조용히 하이픈을 보여준다.
      if (tone === 'muted') return <span className="td-muted">-</span>;
      // valueLabels 가 있으면 값에 맞는 글자를, 없으면 컬럼 이름을 쓴다
      // (품절 여부처럼 뱃지가 켜졌다는 사실 자체가 뜻인 경우).
      return (
        <span className={`badge badge--${tone}`}>{column.valueLabels?.[key] ?? column.label}</span>
      );
    }
    default: {
      // gname(상품명) 등 몇몇 텍스트 칸엔 원본이 남긴 <img> 아이콘이 섞여 있다.
      // 그 태그가 없으면 RichText 도 그냥 글자를 그대로 돌려준다.
      const s = raw === null || raw === undefined || raw === '' ? undefined : String(raw);
      return s ? <RichText value={s} /> : <>{text(null)}</>;
    }
  }
}
